import { defineStore } from 'pinia';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useUpdateStatusStore } from '../updateStatus';
import {
  CastCrewDTO,
  ICastCrewDTO,
  IJudgeSpecialityDTO,
  IPaginationDTO,
  ISettingsDTO,
  JudgeSpecialityDTO,
  SettingsDTO,
} from '@/dtos';
import { useAuthStore } from '../auth';
import {
  createCastAndCrewSettingAPI,
  createJudgeSpecialtiesSettingAPI,
  createSettingAPI,
  deleteCastAndCrewSettingAPI,
  deleteJudgeSpecialtySettingAPI,
  deleteSettingAPI,
  getAllJudgeSpecialitiesSettingsAPI,
  getAllSettingsAPI,
  updateCastAndCrewSettingAPI,
  updateJudgeSpecialtySettingAPI,
  updateSettingAPI,
} from '@/services/admin';
import { computed, Ref } from 'vue';
import { getAllCastAndCrewSettingsAPI } from '@/services/castcrew.service';

export const useSettingsStore = defineStore('settings', () => {
  const queryClient = useQueryClient();
  const statusStore = useUpdateStatusStore();
  const authStore = useAuthStore();

  const getAllSettings = (pagination: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['settings-all', pagination.value]),
      queryFn: async (): Promise<{ data: ISettingsDTO[]; count: number }> => {
        const ret = await getAllSettingsAPI(pagination.value);
        const settings = await Promise.all<ISettingsDTO[]>(
          ret.data.map((settings) => new SettingsDTO(settings)),
        );
        return { data: settings, count: ret.count };
      },
      enabled: computed(() => authStore.isAdmin),
    });
  // Mutations
  const create = useMutation({
    mutationFn: (setting: ISettingsDTO) => {
      return createSettingAPI(setting);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings-all'] });

      statusStore.setSuccessStatus('setting-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`setting-create`, err);
    },
  });

  const update = useMutation({
    mutationFn: (setting: ISettingsDTO) => {
      return updateSettingAPI(setting);
    },
    onSuccess: (_data, setting: ISettingsDTO) => {
      queryClient.invalidateQueries({ queryKey: ['settings-all'] });

      statusStore.setSuccessStatus(`setting-${setting.id}`, 'success:update');
    },
    onError: (err: string, setting) => {
      statusStore.setErrorStatus(`setting-${setting.id}`, err);
    },
  });

  const remove = useMutation({
    mutationFn: (settingId: string) => deleteSettingAPI(settingId),
    onSuccess: (_data, settingId: string) => {
      queryClient.invalidateQueries({ queryKey: ['settings-all'] });
      statusStore.setSuccessStatus(`setting-${settingId}`, 'success:delete');
    },
    onError: (err: string, settingId: string) => {
      statusStore.setErrorStatus(`setting-${settingId}`, err);
    },
  });

  const getAllCastAndCrewSettings = (pagination: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['castAndCrew-settings-all', pagination.value]),
      queryFn: async (): Promise<{ data: ICastCrewDTO[]; count: number }> => {
        const ret = await getAllCastAndCrewSettingsAPI(pagination.value);
        const settings = await Promise.all<ICastCrewDTO[]>(
          ret.data.map((settings) => new CastCrewDTO(settings)),
        );
        return { data: settings, count: ret.count };
      },
      enabled: computed(() => !!pagination.value),
    });

  // Mutations
  const createCastAndCrew = useMutation({
    mutationFn: (setting: ICastCrewDTO) => {
      return createCastAndCrewSettingAPI(setting);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew-settings-all'] });

      statusStore.setSuccessStatus('castAndCrew-setting-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`castAndCrew-setting-create`, err);
    },
  });

  const updateCastAndCrew = useMutation({
    mutationFn: (setting: ICastCrewDTO) => {
      return updateCastAndCrewSettingAPI(setting);
    },
    onSuccess: (_data, setting: ICastCrewDTO) => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew-settings-all'] });

      statusStore.setSuccessStatus(`castAndCrew-setting-${setting.id}`, 'success:update');
    },
    onError: (err: string, setting) => {
      statusStore.setErrorStatus(`castAndCrew-setting-${setting.id}`, err);
    },
  });

  const removeCastAndCrew = useMutation({
    mutationFn: (settingId: string) => deleteCastAndCrewSettingAPI(settingId),
    onSuccess: (_data, settingId: string) => {
      queryClient.invalidateQueries({ queryKey: ['castAndCrew-settings-all'] });
      statusStore.setSuccessStatus(`castAndCrew-setting-${settingId}`, 'success:delete');
    },
    onError: (err: string, settingId: string) => {
      statusStore.setErrorStatus(`castAndCrew-setting-${settingId}`, err);
    },
  });

  const getAllJudgeSpecialitiesSettings = (pagination: Ref<IPaginationDTO>) =>
    useQuery({
      queryKey: computed(() => ['judge-specialities-settings-all', pagination.value]),
      queryFn: async (): Promise<{ data: IJudgeSpecialityDTO[]; count: number }> => {
        const ret = await getAllJudgeSpecialitiesSettingsAPI(pagination.value);
        const settings = await Promise.all<IJudgeSpecialityDTO[]>(
          ret.data.map((settings) => new JudgeSpecialityDTO(settings)),
        );
        return { data: settings, count: ret.count };
      },
      enabled: computed(() => !!pagination.value),
    });

  // Mutations
  const createJudgeSpecialty = useMutation({
    mutationFn: (setting: IJudgeSpecialityDTO) => {
      return createJudgeSpecialtiesSettingAPI(setting);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['judge-specialities-settings-all'] });

      statusStore.setSuccessStatus('judge-specialities-setting-create', 'success:create');
    },
    onError: (err: unknown) => {
      statusStore.setErrorStatus(`judge-specialities-setting-create`, err as string);
    },
  });

  const updateJudgeSpecialty = useMutation({
    mutationFn: (setting: IJudgeSpecialityDTO) => {
      return updateJudgeSpecialtySettingAPI(setting);
    },
    onSuccess: (_data, setting: IJudgeSpecialityDTO) => {
      queryClient.invalidateQueries({ queryKey: ['judge-specialities-settings-all'] });

      statusStore.setSuccessStatus(`judge-specialities-setting-${setting.id}`, 'success:update');
    },
    onError: (err: string, setting) => {
      statusStore.setErrorStatus(`judge-specialities-setting-${setting.id}`, err);
    },
  });

  const removeJudgeSpecialty = useMutation({
    mutationFn: (settingId: string) => deleteJudgeSpecialtySettingAPI(settingId),
    onSuccess: (_data, settingId: string) => {
      queryClient.invalidateQueries({ queryKey: ['judge-specialities-settings-all'] });
      statusStore.setSuccessStatus(`judge-specialities-setting-${settingId}`, 'success:delete');
    },
    onError: (err: string, settingId: string) => {
      statusStore.setErrorStatus(`judge-specialities-setting-${settingId}`, err);
    },
  });

  return {
    getAllSettings,
    getAllCastAndCrewSettings,
    create,
    update,
    remove,
    createCastAndCrew,
    updateCastAndCrew,
    removeCastAndCrew,
    getAllJudgeSpecialitiesSettings,
    createJudgeSpecialty,
    updateJudgeSpecialty,
    removeJudgeSpecialty,
  };
});
